import React, { useEffect, useMemo } from "react"
import { useSelector } from "react-redux"
import { RootState } from "@/reducers/store"
import { useValidate } from "@/state/survey/formSlice"
import InputLabel from "@/components/Atoms/InputLabel"
import InputField from "@/components/Atoms/InputField"
import rightArrow from "@/assets/right.svg"
import { IComponentMetaData } from "../../SurveyModel/types/commonTypes"
import { INode } from "../../SurveyModel/types/nodeTypes"
import { useUpdateAccordionSection } from '@/state/survey/poaSurveySlice'

const HeightWeightBMI_SECTION_KEY = "HeightWeightBMI"

const HEIGHT_KEY = "Height"
const WEIGHT_KEY = "Weight"

const Q1_KEY = "SleepApnoea"
const Q4_KEY = "SnoresLoudly"
const Q5_KEY = "TiredInDaytime"
const Q6_KEY = "SeenToStopBreathingDuringSleep"
const Q7_KEY = "HighBloodPressure"
const Q8_KEY = "NeckCircumference"

const SEX = {
    MALE: "male",
    FEMALE: "female"
} as const

const OSA_RISK_LEVELS = {
    HIGH: "High risk",
    INTERMEDIATE: "Intermediate risk",
    LOW: "Low risk",
} as const

interface ISleepPOA {
    node: INode
    name: string
    disabled: boolean
    metaData?: IComponentMetaData
    repeatedElement?: boolean
    onBlur?: () => void
}

const SleepPOA: React.FC<ISleepPOA> = () => {
    const form = useSelector((state: RootState) => state.form)
    const validateInputField = useValidate()
    const updateAccordionSection = useUpdateAccordionSection()

    const age = Number(form["Age"]?.value) || 0
    const sex = String(form["Sex"]?.value ?? "")
    const heightCM = Number(form[HEIGHT_KEY]?.value) || 0
    const weightKG = Number(form[WEIGHT_KEY]?.value) || 0

    const bmi =
        heightCM > 0 && weightKG > 0
            ? weightKG / ((heightCM / 100) * (heightCM / 100))
            : 0

    const isBmiBlank = !(bmi > 0)

    const q1Value =
        String(form[Q1_KEY]?.value ?? "").toLowerCase() === "yes"

    const q4 = String(form[Q4_KEY]?.value ?? "").toLowerCase() === "yes"
    const q5 = String(form[Q5_KEY]?.value ?? "").toLowerCase() === "yes"
    const q6 = String(form[Q6_KEY]?.value ?? "").toLowerCase() === "yes"
    const q7 = String(form[Q7_KEY]?.value ?? "").toLowerCase() === "yes"
    const q8 = String(form[Q8_KEY]?.value ?? "").toLowerCase() === "yes"

    const handleGoToHeightWeight = (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault()
        const target = document.getElementById(HeightWeightBMI_SECTION_KEY)
        if (target) {
            target.scrollIntoView({ behavior: "smooth", block: "start" })
            updateAccordionSection(HeightWeightBMI_SECTION_KEY, true);
        }
    }

    const stopBangScore = useMemo(() => {
        if (!bmi || bmi <= 0) return 0

        let score = 0

        if (bmi > 35) score++
        if (age > 50) score++
        if (sex.toLowerCase() === SEX.MALE) score++

        if (!q1Value) {
            if (q4) score++
            if (q5) score++
            if (q6) score++
            if (q7) score++
            if (q8) score++
        }

        return score
    }, [bmi, age, sex, q1Value, q4, q5, q6, q7, q8])

    const osaRiskLevel = useMemo(() => {
    if (
        age <= 0 ||
        !sex ||
        bmi <= 0 ||
        form[Q4_KEY]?.value === "" ||
        form[Q5_KEY]?.value === "" ||
        form[Q6_KEY]?.value === "" ||
        form[Q7_KEY]?.value === "" ||
        form[Q8_KEY]?.value === ""
    ) {
        return ""
    }

    if (stopBangScore > 4) return OSA_RISK_LEVELS.HIGH
    if (stopBangScore > 3) return OSA_RISK_LEVELS.INTERMEDIATE
    return OSA_RISK_LEVELS.LOW
}, [stopBangScore, age, sex, bmi, form])

    useEffect(() => {
        const currentValue = form["OSARiskLevel"]?.value ?? ""
    
        if (currentValue !== osaRiskLevel) {
            validateInputField("OSARiskLevel", osaRiskLevel, true)
        }
    }, [osaRiskLevel, form, validateInputField])

    return (
        <div className="sleep-poa-wrap">
            <div className="flex flex-row input-container sleep-poa-fields">
                <div className="user-input w-[120px]">
                    <InputLabel label="Age" labelClasses="input-label" />
                    <InputField
                        disabled
                        form={form}
                        isInValid={false}
                        propClasses="single-line-text flex"
                        type="Number"
                        value={age ? String(age) : ""}
                        name="Age"
                        onChange={() => {}}
                    />
                </div>
                <div className="user-input w-[120px] sleep-poa-sex-field">
                    <InputLabel label="Sex" labelClasses="input-label" />
                    <InputField
                        disabled
                        form={form}
                        isInValid={false}
                        propClasses="single-line-text flex"
                        type="Text"
                        value={sex}
                        name="Sex"
                        onChange={() => {}}
                    />
                </div>
                <div className="user-input w-[120px]">
                    <InputLabel label="BMI" labelClasses="input-label" />
                    <InputField
                        disabled
                        form={form}
                        isInValid={isBmiBlank}
                        showErrors={false}
                        propClasses="single-line-text flex"
                        type="Number"
                        value={bmi > 0 ? bmi.toFixed(1) : ""}
                        name="BMI"
                        onChange={() => {}}
                    />
                    {isBmiBlank && (
                        <div className="error">
                            <div className="error-message">
                                <p>Not known</p>
                            </div>
                        </div>
                    )}
                </div>

            </div>
            {isBmiBlank && (
                <button
                    type="button"
                    onClick={handleGoToHeightWeight}
                    className="sleep-poa-go-to-link"
                >
                    <span>Go to: Height, weight, BMI</span>
                    <img
                        src={rightArrow}
                        alt=""
                        aria-hidden="true"
                        className="sleep-poa-go-to-link-icon"
                    />
                </button>
            )}
        </div>
    )
}

export default SleepPOA